import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { fetchNotifications, markNotificationsAsSeen } from "../../../services/Notification/NotificationApi";
import { useFeatures } from "../../../context/PermissionsAndFeatures/FeaturesContext";

function NotificationList({ token, onViewAll }) {
  const features = useFeatures();
  const theme = useTheme();
  
  // ✅ كشف دقيق لجميع أحجام التابلت
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  
  // ✅ كشف الوضع الأفقي/العمودي
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");
  
  const isDark = theme.palette.mode === "dark";

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ حساب أحجام العناصر حسب حجم الشاشة
  const getContainerWidth = () => {
    if (isSmallTablet) return isPortrait ? 320 : 350;
    if (isMediumTablet) return isPortrait ? 360 : 380;
    return isPortrait ? 400 : 450;
  };

  const getMaxHeight = () => {
    if (isSmallTablet) return isPortrait ? 400 : 450;
    if (isMediumTablet) return isPortrait ? 450 : 500;
    return isPortrait ? 480 : 520;
  };

  const getHeaderIconSize = () => {
    if (isSmallTablet) return 20;
    if (isMediumTablet) return 22;
    return 24;
  };

  const getHeaderFontSize = () => {
    if (isSmallTablet) return "16px";
    if (isMediumTablet) return "17px";
    return "18px";
  };

  const getButtonFontSize = () => {
    if (isSmallTablet) return "11px";
    if (isMediumTablet) return "11.5px";
    return "12px";
  };

  const getTitleFontSize = () => {
    if (isSmallTablet) return "13px";
    if (isMediumTablet) return "13.5px";
    return "14px";
  };

  const getMessageFontSize = () => {
    if (isSmallTablet) return "12px";
    if (isMediumTablet) return "12.5px";
    return "13px";
  };

  const getCaptionFontSize = () => {
    if (isSmallTablet) return "10px";
    if (isMediumTablet) return "10.5px";
    return "11px";
  };

  const getCardPadding = () => {
    if (isSmallTablet) return 1.2;
    if (isMediumTablet) return 1.3;
    return 1.5;
  };

  // Note: do not return early here — keep Hooks order stable.

  const resolveFeatureFlag = (featObj, key) => {
    if (featObj == null) return null; // unknown
    try {
      if (typeof featObj === "function") return featObj(key);
      if (typeof featObj.isEnabled === "function") return featObj.isEnabled(key);
      if (typeof featObj.hasFeature === "function") return featObj.hasFeature(key);
      if (typeof featObj.get === "function") return featObj.get(key);
      if (key in featObj) return featObj[key];
    } catch (e) {
      return null;
    }
    return null;
  };

  // compute flag (لا يُستخدم للـ early return هنا لتجنب استدعاء Hooks بشكلٍ شرطي)
  const rawNotificationsFlag = resolveFeatureFlag(features, "notifications");
  const isNotificationsEnabled = rawNotificationsFlag === false ? false : true;

  // دالة مُعاد استخدامها لجلب الإشعارات وتحديث الحالة (تُستخدم أيضاً بعد تعليم الكل كمقروء)
  const loadNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchNotifications(token);
      const notifications = (res?.data?.notifications || [])
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(notifications);
    } catch (err) {
      setError("فشل في جلب الإشعارات");
    } finally {
      setLoading(false);
    }
  };

  // useEffect يُعرف هنا (غير مشروط) ويعتمد على isNotificationsEnabled لعدم التنفيذ إذا كانت الميزة معطّلة
  useEffect(() => {
    if (token && isNotificationsEnabled) loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isNotificationsEnabled]);

  // Ensure we don't render on non-tablet devices.
  // Placed after all Hooks so ESLint react-hooks/rules-of-hooks is satisfied.
  if (!isTablet) {
    return null;
  }

  const handleMarkAsSeen = async (id) => {
    try {
      await markNotificationsAsSeen(token, [id]);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, seen: 1 } : n))
      );
    } catch {
      console.error("Failed to mark as seen");
    }
  };

  const handleViewAll = async () => {
    if (!token) {
      if (onViewAll) onViewAll();
      return;
    }

    const unseenIds = notifications
      .filter((n) => !n.seen || n.seen === 0)
      .map((n) => n.id);

    if (unseenIds.length === 0) {
      if (onViewAll) onViewAll();
      else await loadNotifications();
      return;
    }

    const prev = notifications;

    setNotifications((prevList) =>
      prevList.map((n) => (unseenIds.includes(n.id) ? { ...n, seen: 1 } : n))
    );

    try {
      await markNotificationsAsSeen(token, unseenIds);
      await loadNotifications();
      if (onViewAll) onViewAll();
    } catch (err) {
      setNotifications(prev);
      alert("فشل في تعليم التنبيهات كمقروءة. حاول مرة أخرى.");
    }
  };

  // ✅ إذا كانت الميزة معطّلة
  if (isNotificationsEnabled === false) {
    return (
      <Box
        sx={{
          minWidth: getContainerWidth(),
          maxWidth: getContainerWidth() + 50,
          p: isSmallTablet ? 2 : 3,
          bgcolor: isDark ? "#23272f" : "#fff",
          borderRadius: isSmallTablet ? 1.5 : 2,
        }}
      >
        <Alert 
          severity="warning" 
          sx={{ 
            borderRadius: isSmallTablet ? 1.5 : 2,
            "& .MuiAlert-icon": {
              fontSize: isSmallTablet ? 20 : 22,
            },
          }}
        >
          <Typography 
            fontWeight="bold" 
            mb={1}
            sx={{ fontSize: isSmallTablet ? "13px" : "14px" }}
          >
            🚫 خدمة الإشعارات غير مفعّلة
          </Typography>
          <Typography 
            variant="body2"
            sx={{ fontSize: isSmallTablet ? "12px" : "13px" }}
          >
            يرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الإشعارات.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minWidth: getContainerWidth(),
          maxWidth: getContainerWidth() + 50,
          p: isSmallTablet ? 2 : 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: isSmallTablet ? 150 : 200,
        }}
      >
        <CircularProgress size={isSmallTablet ? 36 : 40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          minWidth: getContainerWidth(), 
          maxWidth: getContainerWidth() + 50,
          p: isSmallTablet ? 2 : 3,
        }}
      >
        <Alert 
          severity="error"
          sx={{ fontSize: isSmallTablet ? "12px" : "13px" }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: getContainerWidth(),
        maxWidth: getContainerWidth() + 50,
        maxHeight: getMaxHeight(),
        display: "flex",
        flexDirection: "column",
        bgcolor: isDark ? "#23272f" : "#fff",
        borderRadius: isSmallTablet ? 1.5 : 2,
        overflow: "hidden",
        boxShadow: isDark 
          ? "0 8px 24px rgba(0,0,0,0.4)" 
          : "0 8px 24px rgba(0,0,0,0.1)",
        // ✅ تحسين الأداء
        willChange: "transform",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: isSmallTablet ? 1.5 : 2,
          bgcolor: isDark ? "rgba(30, 30, 30, 0.8)" : "rgba(0, 0, 0, 0.02)",
          borderBottom: `1px solid ${isDark ? "#333" : "#eee"}`,
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: isSmallTablet ? 0.8 : 1 }}>
          <NotificationsActiveIcon
            sx={{
              color: "#EA7C69",
              fontSize: getHeaderIconSize(),
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
            }}
          />
          <Typography 
            fontWeight="bold" 
            fontSize={getHeaderFontSize()}
            sx={{ lineHeight: 1.2 }}
          >
            الإشعارات
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleViewAll}
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            fontSize: getButtonFontSize(),
            fontWeight: 600,
            px: isSmallTablet ? 1.2 : 1.5,
            py: isSmallTablet ? 0.4 : 0.5,
            minHeight: 32,
            textTransform: "none",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: "#1565c0",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          رؤية الكل
        </Button>
      </Box>

      {/* Notifications List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: isSmallTablet ? 0.8 : 1,
          display: "flex",
          flexDirection: "column",
          gap: isSmallTablet ? 0.8 : 1,
          // ✅ تحسين scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: isDark ? "#444 transparent" : "#ccc transparent",
          "&::-webkit-scrollbar": {
            width: isSmallTablet ? "4px" : "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: isDark ? "#444" : "#ccc",
            borderRadius: "3px",
            "&:hover": {
              bgcolor: isDark ? "#555" : "#bbb",
            },
          },
          // ✅ تحسين الأداء
          WebkitOverflowScrolling: "touch",
        }}
      >
        {notifications.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: isSmallTablet ? 3 : 4,
              color: isDark ? "#888" : "#999",
            }}
          >
            <NotificationsActiveIcon 
              sx={{ 
                fontSize: isSmallTablet ? 40 : 48, 
                mb: 1, 
                opacity: 0.5 
              }} 
            />
            <Typography 
              variant="body2"
              sx={{ fontSize: isSmallTablet ? "12px" : "13px" }}
            >
              لا توجد إشعارات
            </Typography>
          </Box>
        ) : (
          notifications.map((n, idx) => (
            <Box key={n.id}>
              <Box
                sx={{
                  p: getCardPadding(),
                  borderRadius: isSmallTablet ? 1.2 : 1.5,
                  bgcolor: n.seen
                    ? "transparent"
                    : isDark
                    ? "rgba(25, 118, 210, 0.1)"
                    : "rgba(25, 118, 210, 0.05)",
                  border: `1px solid ${
                    n.seen
                      ? isDark
                        ? "#333"
                        : "#eee"
                      : isDark
                      ? "#1976d2"
                      : "#90caf9"
                  }`,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  // ✅ تحسين touch targets
                  minHeight: 44,
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": {
                    bgcolor: n.seen
                      ? isDark
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.02)"
                      : isDark
                      ? "rgba(25, 118, 210, 0.15)"
                      : "rgba(25, 118, 210, 0.08)",
                    transform: "translateX(-2px)",
                  },
                }}
              >
                {/* Title + Mark as Seen Button */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    mb: isSmallTablet ? 0.6 : 0.8,
                    gap: isSmallTablet ? 0.8 : 1,
                  }}
                >
                  <Typography
                    fontWeight={n.seen ? 500 : 700}
                    fontSize={getTitleFontSize()}
                    sx={{
                      color: n.seen ? (isDark ? "#bbb" : "#666") : isDark ? "#fff" : "#222",
                      flex: 1,
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {n.title}
                  </Typography>
                  {!n.seen && (
                    <IconButton
                      size="small"
                      onClick={() => handleMarkAsSeen(n.id)}
                      title="تعليم كمقروء"
                      sx={{
                        color: "#43a047",
                        width: isSmallTablet ? 28 : 32,
                        height: isSmallTablet ? 28 : 32,
                        transition: "all 0.2s",
                        "&:hover": { 
                          bgcolor: "rgba(67, 160, 71, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <CheckCircleIcon fontSize={isSmallTablet ? "small" : "medium"} />
                    </IconButton>
                  )}
                  {n.seen && (
                    <Chip
                      label="مقروء"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: isSmallTablet ? "18px" : "20px",
                        fontSize: getCaptionFontSize(),
                        color: isDark ? "#888" : "#999",
                        "& .MuiChip-label": {
                          px: 0.8,
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Message */}
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? "#d6e0f0" : "#333",
                    mb: isSmallTablet ? 0.8 : 1,
                    lineHeight: 1.4,
                    fontSize: getMessageFontSize(),
                    wordBreak: "break-word",
                  }}
                >
                  {n.message}
                </Typography>

                {/* Details */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: isSmallTablet ? 0.4 : 0.5,
                    mb: isSmallTablet ? 0.6 : 0.8,
                    fontSize: getCaptionFontSize(),
                    color: isDark ? "#999" : "#666",
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: getCaptionFontSize() }}>
                    من: {n.from_user_id ?? "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: getCaptionFontSize() }}>
                    إلى: {n.to_user_id ?? "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: getCaptionFontSize() }}>
                    الطلب: {n.order_id ?? "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: getCaptionFontSize() }}>
                    النوع: {n.type ?? "-"}
                  </Typography>
                </Box>

                {/* Date */}
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "#666" : "#999",
                    fontSize: getCaptionFontSize(),
                    lineHeight: 1.2,
                  }}
                >
                  {new Date(n.created_at).toLocaleString("ar-EG")}
                </Typography>
              </Box>
              {idx < notifications.length - 1 && (
                <Divider 
                  sx={{ 
                    my: isSmallTablet ? 0.4 : 0.5, 
                    opacity: 0.5 
                  }} 
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export default NotificationList;
